<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CopilotRoutinesAPI from 'dashboard/api/copilot/routines';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close', 'runRoutine']);

const { t } = useI18n();
const routines = ref([]);
const isFetching = ref(false);

const headerButtons = computed(() => [
  {
    key: 'refresh',
    icon: 'i-ri-restart-line',
    tooltip: t('CAPTAIN.COPILOT.ROUTINES.REFRESH'),
  },
]);

const fetchRoutines = async () => {
  isFetching.value = true;
  try {
    const { data } = await CopilotRoutinesAPI.get();
    routines.value = data.routines || [];
  } catch (error) {
    useAlert(error.message);
  } finally {
    isFetching.value = false;
  }
};

const handleHeaderAction = action => {
  if (action === 'refresh') fetchRoutines();
};

const runRoutine = routine => {
  emit('runRoutine', routine.prompt);
};

onMounted(fetchRoutines);
</script>

<template>
  <div class="flex flex-col h-full min-h-0 w-full text-sm">
    <SidebarActionsHeader
      :title="t('CAPTAIN.COPILOT.ROUTINES.TITLE')"
      :buttons="headerButtons"
      @click="handleHeaderAction"
      @close="emit('close')"
    />

    <div class="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
      <p v-if="isFetching" class="text-xs text-n-slate-10">
        {{ t('CAPTAIN.COPILOT.ROUTINES.LOADING') }}
      </p>

      <p v-else-if="!routines.length" class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.COPILOT.ROUTINES.EMPTY') }}
      </p>

      <template v-else>
        <div
          v-for="routine in routines"
          :key="routine.id"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-3 space-y-2"
        >
          <div>
            <div class="text-sm font-medium text-n-slate-12 truncate">
              {{ routine.name }}
            </div>
            <p
              v-if="routine.description"
              class="mt-0.5 text-xs text-n-slate-11 line-clamp-2"
            >
              {{ routine.description }}
            </p>
          </div>
          <Button
            :label="t('CAPTAIN.COPILOT.ROUTINES.RUN')"
            sm
            teal
            faded
            @click="runRoutine(routine)"
          />
        </div>
      </template>
    </div>
  </div>
</template>
